/* ============================================
   WORK ORB COMPONENT
   The Creative Orchestra - GEMINI Framework
   Individual work orb for the orbital system
   ============================================ */

import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useOrbStyles } from '@hooks/useWorkTheme';
import { works } from '@data';

/**
 * Work Orb Component
 * Represents a single work in the orbital system
 * 
 * @param {Object} props
 * @param {string} props.workId - Work identifier
 * @param {number} props.angle - Position angle in radians
 * @param {number} props.radius - Distance from center
 * @param {number} props.size - Orb size in pixels
 * @param {number} props.index - Index for stagger animations
 */
const WorkOrb = ({
  workId,
  angle = 0,
  radius = 280,
  size = 80,
  index = 0,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  
  // Get work data
  const work = useMemo(() => works[workId], [workId]);
  
  // Get themed styles
  const { containerStyles, labelStyles, ringStyles, accentColor } = useOrbStyles(workId, isHovered);

  if (!work) return null;

  // Calculate position
  const x = Math.cos(angle) * radius;
  const y = Math.sin(angle) * radius;

  return (
    <motion.div
      className="absolute"
      style={{
        left: '50%',
        top: '50%',
        width: size,
        height: size,
        marginLeft: -size / 2,
        marginTop: -size / 2,
      }}
      initial={{ 
        opacity: 0, 
        scale: 0,
        x,
        y,
      }}
      animate={{ 
        opacity: 1, 
        scale: 1,
        x,
        y,
      }}
      transition={{
        delay: 0.5 + index * 0.1,
        duration: 0.6,
        type: 'spring',
        stiffness: 200,
        damping: 20,
      }}
    >
      <Link
        to={`/work/${workId}`}
        className="block relative w-full h-full group"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Outer ring (hover effect) */}
        <motion.div
          className="absolute inset-0 rounded-full border-2 pointer-events-none"
          style={ringStyles}
          initial={false}
          animate={{
            scale: isHovered ? 1.3 : 1,
            opacity: isHovered ? 0.6 : 0,
          }}
          transition={{ duration: 0.3 }}
        />

        {/* Pulse ring animation */}
        {isHovered && (
          <motion.div
            className="absolute inset-0 rounded-full border pointer-events-none"
            style={{ borderColor: accentColor }}
            initial={{ scale: 1, opacity: 0.8 }}
            animate={{ scale: 1.8, opacity: 0 }}
            transition={{ duration: 1, repeat: Infinity }}
          />
        )}

        {/* Main orb */}
        <motion.div
          className="relative w-full h-full rounded-full flex items-center justify-center
            border border-white/20 cursor-pointer overflow-hidden"
          style={containerStyles}
          whileHover={{ scale: 1.15 }}
          whileTap={{ scale: 0.95 }}
          transition={{ duration: 0.3 }}
        >
          {/* Inner glow */}
          <div 
            className="absolute inset-0 rounded-full opacity-50"
            style={{
              background: `radial-gradient(circle at 30% 30%, rgba(255,255,255,0.3) 0%, transparent 60%)`,
            }}
          />

          {/* Work name */}
          <motion.span
            className="relative z-10 text-xs md:text-sm font-bold tracking-wider"
            style={labelStyles}
          >
            {work.name}
          </motion.span>
        </motion.div>

        {/* Tooltip on hover */}
        <motion.div
          className="absolute left-1/2 -translate-x-1/2 pointer-events-none
            whitespace-nowrap px-3 py-2 rounded-lg
            bg-black/80 backdrop-blur-sm border border-white/10
            text-center z-20"
          style={{
            top: size + 10,
          }}
          initial={{ opacity: 0, y: -10 }}
          animate={{ 
            opacity: isHovered ? 1 : 0, 
            y: isHovered ? 0 : -10,
          }}
          transition={{ duration: 0.2 }}
        >
          <p className="text-white text-sm font-medium">{work.name}</p>
          <p className="text-gray-400 text-xs mt-0.5">{work.stage}</p>
        </motion.div>
      </Link>
    </motion.div>
  );
};

/**
 * Static Work Orb (for non-orbital displays)
 */
export const StaticWorkOrb = ({
  workId,
  size = 100,
  showLabel = true,
  className = '',
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const work = useMemo(() => works[workId], [workId]);
  const { containerStyles, labelStyles, accentColor } = useOrbStyles(workId, isHovered);

  if (!work) return null;

  return (
    <Link
      to={`/work/${workId}`}
      className={`relative inline-block ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <motion.div
        className="rounded-full flex items-center justify-center
          border border-white/20 cursor-pointer overflow-hidden"
        style={{
          width: size,
          height: size,
          ...containerStyles,
        }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
      >
        <div 
          className="absolute inset-0 rounded-full opacity-50"
          style={{
            background: `radial-gradient(circle at 30% 30%, rgba(255,255,255,0.3) 0%, transparent 60%)`,
          }}
        />
        <span 
          className="relative z-10 font-bold tracking-wider"
          style={{
            ...labelStyles,
            fontSize: size > 80 ? '0.875rem' : '0.75rem',
          }}
        >
          {work.name}
        </span>
      </motion.div>
      
      {showLabel && (
        <motion.p
          className="text-center mt-2 text-sm text-gray-400"
          animate={{ color: isHovered ? accentColor : 'rgb(156 163 175)' }}
        >
          {work.fullName}
        </motion.p>
      )}
    </Link>
  );
};

/**
 * Work Orb Grid (for displaying all works in a grid)
 */
export const WorkOrbGrid = ({ 
  orbSize = 80,
  className = '',
}) => {
  const workIds = Object.keys(works);

  return (
    <div className={`grid grid-cols-4 gap-8 ${className}`}>
      {workIds.map((workId, index) => (
        <motion.div
          key={workId}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
          className="flex flex-col items-center"
        >
          <StaticWorkOrb workId={workId} size={orbSize} showLabel />
        </motion.div>
      ))}
    </div>
  );
};

export default WorkOrb;
